import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/base/Breadcrumb";
import Toast from "@/components/base/Toast";
import WorkCard from "@/components/base/WorkCard";
import ReviewList from "@/pages/recommendations/detail/components/ReviewList";
import { getRelatedWorks, getWorkById } from "@/utils/anime";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useState } from "react";

export default function WorkDetailPage() {
  const { id } = useParams<{ id: string }>();
  const work = id ? getWorkById(id) : undefined;
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState<string | null>(null);

  usePageMeta(
    work ? `${work.title} - 78动漫` : "作品未找到 - 78动漫",
    work ? `${work.title}：${work.synopsis.slice(0, 60)}` : "未找到对应的动漫作品，请返回作品推荐列表浏览。",
    work ? `${work.title},${work.genres.join(",")},动漫推荐,78动漫` : "78动漫,动漫推荐",
  );

  if (!work) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 items-center justify-center px-4 py-24">
          <div className="flex max-w-md flex-col items-center text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-movie-line text-3xl" />
            </span>
            <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">未找到该作品</h1>
            <p className="mt-2 text-sm text-foreground-500">这部作品可能已被移除或链接有误。</p>
            <Link
              to="/recommendations"
              className="mt-6 flex items-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
              返回作品推荐
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = getRelatedWorks(work, 6);
  const favorited = isFavorite(work.id);

  const metaItems = [
    { label: "作品类型", value: work.type, icon: "ri-movie-2-line" },
    { label: "制作公司", value: work.studio, icon: "ri-building-2-line" },
    { label: "原作者 / 导演", value: work.author, icon: "ri-quill-pen-line" },
    { label: "首播年份", value: String(work.year), icon: "ri-calendar-line" },
    { label: "地区", value: work.region, icon: "ri-map-pin-line" },
    { label: "集数 / 时长", value: work.episodeCount, icon: "ri-time-line" },
    { label: "状态", value: work.status, icon: "ri-play-circle-line" },
  ];

  const handleFavorite = () => {
    toggleFavorite(work.id);
    setToast(favorited ? "已取消收藏（本地演示）" : "已加入收藏（本地演示，刷新后保留）");
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <div className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-4 md:px-8">
            <Breadcrumb
              items={[
                { label: "动漫推荐", to: "/recommendations" },
                { label: work.title },
              ]}
            />
          </div>
        </div>

        <section className="w-full border-b border-background-200 bg-background-50">
          <div className="mx-auto max-w-7xl px-4 py-8 md:px-8 md:py-12">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
              <div className="lg:col-span-4">
                <div className="mx-auto w-full max-w-xs overflow-hidden rounded-lg border border-background-200 bg-background-100 lg:max-w-none">
                  <img
                    src={work.cover}
                    alt={`${work.title} 作品封面`}
                    title={`${work.title} - 78动漫作品封面`}
                    className="h-[420px] w-full object-cover object-top md:h-[520px]"
                  />
                </div>
              </div>

              <div className="lg:col-span-8">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-md bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-700">
                    {work.type}
                  </span>
                  {work.genres.map((genre) => (
                    <span
                      key={genre}
                      className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-800"
                    >
                      {genre}
                    </span>
                  ))}
                </div>

                <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
                  {work.title}
                </h1>

                <div className="mt-4 flex flex-wrap items-center gap-5">
                  <div className="flex items-center gap-2 rounded-lg bg-accent-50 px-4 py-2">
                    <i className="ri-star-fill text-xl text-accent-500" />
                    <span className="font-heading text-2xl font-bold text-accent-700">{work.score.toFixed(1)}</span>
                    <span className="text-xs text-accent-700/70">综合评分</span>
                  </div>
                  <button
                    type="button"
                    onClick={handleFavorite}
                    className={`flex items-center gap-2 whitespace-nowrap rounded-md px-5 py-3 text-sm font-medium transition-colors cursor-pointer ${
                      favorited
                        ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                        : "border border-background-300 bg-background-50 text-foreground-800 hover:border-accent-300 hover:text-accent-600"
                    }`}
                  >
                    <i className={favorited ? "ri-heart-fill text-lg" : "ri-heart-line text-lg"} />
                    {favorited ? "已收藏" : "收藏作品"}
                  </button>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-3">
                  {metaItems.map((item) => (
                    <div key={item.label} className="rounded-lg border border-background-200 bg-background-50 p-3">
                      <p className="flex items-center gap-1.5 text-xs text-foreground-400">
                        <i className={item.icon} />
                        {item.label}
                      </p>
                      <p className="mt-1 line-clamp-1 text-sm font-medium text-foreground-900" title={item.value}>
                        {item.value}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="mt-6">
                  <h2 className="font-heading text-base font-bold text-foreground-950">剧情简介</h2>
                  <p className="mt-3 text-[15px] leading-loose text-foreground-700">{work.synopsis}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-10 md:px-8 md:py-12">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <i className="ri-sparkling-2-line text-xl text-accent-500" />
                作品特色
              </h2>
              <ul className="mt-5 flex flex-col gap-3">
                {work.highlights.map((highlight) => (
                  <li
                    key={highlight}
                    className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4"
                  >
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-check-line text-sm" />
                    </span>
                    <span className="text-sm leading-relaxed text-foreground-700">{highlight}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-7">
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <i className="ri-chat-quote-line text-xl text-primary-600" />
                观众短评
                <span className="text-xs font-normal text-foreground-400">（虚构示范）</span>
              </h2>
              <div className="mt-5">
                <ReviewList reviews={work.reviews} />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full border-t border-background-200 bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-14">
            <div className="mb-6 flex items-end justify-between gap-3">
              <div>
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关作品推荐</h2>
                <p className="mt-1 text-sm text-foreground-500">基于题材与制作团队的相似度推荐</p>
              </div>
              <Link
                to="/recommendations"
                className="flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50 cursor-pointer"
              >
                更多作品
                <i className="ri-arrow-right-s-line text-lg" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
              {related.map((item) => (
                <WorkCard key={item.id} work={item} />
              ))}
            </div>
          </div>
        </section>
      </main>
      <Toast message={toast} onClose={() => setToast(null)} />
      <SiteFooter />
    </div>
  );
}